import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Tooltip,
  Avatar,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  Share as ShareIcon,
  GroupAdd as GroupAddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Card } from './Card';
import { getOrganizationUsers } from '../services/api';

interface User {
  user_id: string;
  user_name: string;
  user_email: string;
  is_active: boolean; // Add is_active property
}

interface SharedUser {
  id: string;
  name: string;
  email: string;
  role?: string;
  shared_deal_id?: string;
  shared_fund_id?: string;
}

interface SharingApi {
  getSharedWithUsers: (id: string) => Promise<SharedUser[]>;
  share: (params: { deal_id?: string; fund_id?: string; shared_with_id: string }) => Promise<any>;
  unshare: (id: string) => Promise<any>;
}

interface SharingTabProps {
  entityId: string;
  sharingApi: SharingApi;
  entityName: string;
}

export const SharingTab: React.FC<SharingTabProps> = ({ entityId, sharingApi, entityName }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [sharedWith, setSharedWith] = useState<SharedUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [unsharingId, setUnsharingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const organization = localStorage.getItem('organization');
        if (organization) {
          const org = JSON.parse(organization);
          const usersPromise = getOrganizationUsers(org.id);
          const sharedUsersPromise = sharingApi.getSharedWithUsers(entityId);

          const [usersDataResponse, sharedUsers] = await Promise.all([usersPromise, sharedUsersPromise]);
          const usersData = usersDataResponse.members || []; // Access the members array
          
          setUsers(usersData);
          setSharedWith(sharedUsers);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [entityId, sharingApi]);

  const handleShare = async () => {
    if (!selectedUser) return;
    setIsSharing(true);
    try {
      const shareParams: { [key: string]: string } = { shared_with_id: selectedUser };
      if (entityName === 'deal') {
        shareParams.deal_id = entityId;
      } else if (entityName === 'fund') {
        shareParams.fund_id = entityId;
      }
      await sharingApi.share(shareParams as any);
      const updatedSharedWith = await sharingApi.getSharedWithUsers(entityId);
      setSharedWith(updatedSharedWith);
      setSelectedUser('');
      setShareDialogOpen(false);
    } catch (error) {
      console.error(`Failed to share ${entityName}:`, error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleUnshare = async (sharedEntityId: string) => {
    setUnsharingId(sharedEntityId);
    try {
      await sharingApi.unshare(sharedEntityId);
      const updatedSharedWith = await sharingApi.getSharedWithUsers(entityId);
      setSharedWith(updatedSharedWith);
    } catch (error) {
      console.error(`Failed to unshare ${entityName}:`, error);
    } finally {
      setUnsharingId(null);
    }
  };

  const usersToShare = users.filter(orgUser =>
    orgUser.is_active && !sharedWith.some(sharedEntity => sharedEntity.email === orgUser.user_email)
  );

  return (
    <Box>
      <Card>
        <Card.Header
          title={`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} Sharing`}
          subtitle={`Manage who has access to this ${entityName}`}
          action={
            <Button
              variant="contained"
              startIcon={<GroupAddIcon />}
              onClick={() => setShareDialogOpen(true)}
            >
              Share {entityName.charAt(0).toUpperCase() + entityName.slice(1)}
            </Button>
          }
        />
        <Card.Content>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sharedWith.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sharedWith.map((sharedItem) => (
                    <TableRow key={sharedItem.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2 }}>
                            {sharedItem.email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{sharedItem.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{sharedItem.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={sharedItem.role || 'Viewer'} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove access">
                          <IconButton
                            edge="end"
                            aria-label="unshare"
                            onClick={() => handleUnshare(sharedItem.shared_deal_id || sharedItem.shared_fund_id || '')}
                            disabled={unsharingId === (sharedItem.shared_deal_id || sharedItem.shared_fund_id)}
                          >
                            {unsharingId === (sharedItem.shared_deal_id || sharedItem.shared_fund_id) ? <CircularProgress size={20} /> : <DeleteIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GroupAddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                This {entityName} is private
              </Typography>
              <Typography color="text.secondary">
                Share this {entityName} with your team members to collaborate.
              </Typography>
            </Box>
          )}
        </Card.Content>
      </Card>

      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share {entityName.charAt(0).toUpperCase() + entityName.slice(1)} with a Team Member</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={usersToShare}
            getOptionLabel={(option) => `${option.user_name} (${option.user_email})`}
            onChange={(event, newValue) => {
              setSelectedUser(newValue ? newValue.user_id : '');
            }}
            renderInput={(params) => <TextField {...params} label="Select a user" margin="normal" fullWidth />}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleShare}
            disabled={!selectedUser || isSharing}
            variant="contained"
            startIcon={isSharing ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
          >
            {isSharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};